import { CardDetailTemplate } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { MBCardChannelProvider } from '@shared/components/blocks/contexts/channel';
import { CommonFields, Fields } from '@shared/components/blocks/types';
import { MBLoadingIndicator } from '@shared/components/loading';
import { useUpdateCard } from '@shared/scenes/cards/form/hooks';
import { useCard, useCardDefinition, useCardTemplate } from '@shared/scenes/cards/hooks';
import { useViewTemplateWithUITemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { useChannel } from '@shared/util/hooks';
import { useNavigator } from '@shared/util/navigation';
import { usePermission } from '@shared/util/permissions/hooks';
import { isNoCodeCard } from '@shared/util/workflow';
import { MBCardDetailsComponent } from '../components';

interface InternalProps {
  card: NonNullable<API.CardDetailsQuery['card']>;
  channelSlug: string;
  templateSlug: string | undefined;
  handleEdit: () => void;
  handleSubmit: (fields: Fields & CommonFields) => void;
}

interface Props {
  cardId: string;
  channelSlug: string;
  templateSlug?: string;
}

function useCheckAccess(cardId: string, channelSlug: string) {
  const { card, error } = useCard(cardId, { fetchPolicy: 'cache-and-network' });
  const navigator = useNavigator();
  const noAccess = !card && error?.message === 'Forbidden resource';
  useEffect(() => {
    if (noAccess) {
      navigator.replace('CardDetailsNoAccess', { channelSlug });
    }
  }, [noAccess, navigator, channelSlug]);
}

function useCheckIfDetailsExist(
  cardId: string,
  channelSlug: string,
  loading: boolean,
  template: CardDetailTemplate | undefined,
) {
  const navigator = useNavigator();
  useEffect(() => {
    if (!loading && !template) {
      navigator.replace('CardUpdate', { cardId, channelSlug });
    }
  }, [loading, template, navigator, cardId, channelSlug]);
}

interface LowCodeComponentProps extends InternalProps {
  channel?: NonNullable<API.ChannelQuery['channel']>;
}

function LowCodeComponent({
  card,
  channel,
  channelSlug,
  templateSlug,
  handleEdit,
  handleSubmit,
}: LowCodeComponentProps) {
  const { cardDefinition } = useCardDefinition(
    channel?.id,
    card.workflowTemplateId ?? undefined,
    card.cardDefinitionId,
  );

  const { template, loading } = useCardTemplate<CardDetailTemplate>({
    channelSlug: channelSlug,
    templateSlug,
    context: isNil(templateSlug) ? 'detail' : undefined,
    cardDefinition,
  });

  useCheckIfDetailsExist(card.id, channelSlug, loading, template ?? undefined);

  if (loading || isNil(template)) {
    return <MBLoadingIndicator fill />;
  }

  return (
    <MBCardDetailsComponent
      card={card}
      template={template}
      onEdit={handleEdit}
      onSubmit={handleSubmit}
    />
  );
}

function NoCodeComponent({
  card,
  channelSlug,
  templateSlug,
  handleEdit,
  handleSubmit,
}: InternalProps) {
  const { viewTemplate, loading } = useViewTemplateWithUITemplate(
    card.workflowTemplateId ?? undefined,
    templateSlug,
  );

  const template = viewTemplate?.cardUITemplate?.template as CardDetailTemplate | undefined;

  useCheckIfDetailsExist(card.id, channelSlug, loading, template);

  if (loading || isNil(template)) {
    return <MBLoadingIndicator fill />;
  }

  return (
    <MBCardDetailsComponent
      card={card}
      template={template}
      onEdit={handleEdit}
      onSubmit={handleSubmit}
    />
  );
}

function CardDetails({ cardId, channelSlug, templateSlug }: Props) {
  const { card } = useCard(cardId, { fetchPolicy: 'cache-and-network' });
  const channel = useChannel({ channelSlug });
  const permission = usePermission({ action: 'update', subject: 'Card', channelId: channel?.id });
  const navigator = useNavigator();
  const navigateToCardUpdate = useCallback(
    () =>
      navigator.navigate('CardUpdate', {
        cardId,
        channelSlug,
        templateSlug,
        workflowTemplateId: channel?.workflowTemplateId ?? undefined,
      }),
    [navigator, cardId, channelSlug, templateSlug, channel?.workflowTemplateId],
  );
  const updateCard = useUpdateCard();

  const handleEdit = useCallback(
    () => (permission.enabled ? navigateToCardUpdate() : () => {}),
    [permission.enabled, navigateToCardUpdate],
  );

  const handleSubmit = useCallback(
    async (fields: Fields & CommonFields) => {
      if (!isNil(card)) {
        await updateCard(card, fields);
      }
    },
    [card, updateCard],
  );

  useCheckAccess(cardId, channelSlug);

  if (isNil(card)) {
    return <MBLoadingIndicator fill />;
  }

  const isNoCode = isNoCodeCard(card);

  const commonProps: InternalProps = {
    card,
    channelSlug,
    templateSlug,
    handleEdit,
    handleSubmit,
  };

  return isNoCode ? (
    <NoCodeComponent {...commonProps} />
  ) : (
    <LowCodeComponent {...commonProps} channel={channel} />
  );
}

export function MBCardDetails(props: Props) {
  return (
    <MBCardChannelProvider channelSlug={props.channelSlug}>
      <CardDetails {...props} />
    </MBCardChannelProvider>
  );
}
